// Firestore data models and service functions
import { db } from '../firebase';
import { 
  collection, 
  collectionGroup,
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { CLIENT_LIST_BUCKET_FIELD, computeClientListBucket } from '../utils/clientListBucket';
import { ensureClientStandardFoldersQuietly } from './clientDocumentsGenerateService';

// Collections
const CLIENTS_COLLECTION = 'clients';

// Client data model
export const createClientRecord = async (userId, extractedData) => {
  try {
    const clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Import user team service for auto-assignment and user name
    const userTeamServiceModule = await import('./userTeamService');
    const userTeamService = userTeamServiceModule.default;
    const userName = userTeamService.getUserName(userId);
    const defaultAssignments = userTeamService.getDefaultAssignments(userId);
    
    const clientRecord = {
      // Metadata
      id: clientId,
      createdBy: userId,
      createdByName: userName,
      createdAt: serverTimestamp(),
      lastModifiedBy: userId,
      lastModifiedByName: userName,
      lastModified: serverTimestamp(),
      
      // Team Assignments - Auto-assigned based on creator; caller may override via extractedData.assignment
      assignment: {
        ...(extractedData.assignment || {}),
        assignedMarketingTeam: extractedData.assignment?.assignedMarketingTeam ?? defaultAssignments.assignedMarketingTeam,
        assignedMarketer: extractedData.assignment?.assignedMarketer ?? defaultAssignments.assignedMarketer ?? null,
        assignedAdvisory: extractedData.assignment?.assignedAdvisory ?? defaultAssignments.assignedAdvisory,
        assignedAdviser: extractedData.assignment?.assignedAdviser ?? null,
        assignedAdviserName: extractedData.assignment?.assignedAdviserName ?? null,
        claimedByParaplanner: extractedData.assignment?.claimedByParaplanner ?? null,
        claimedByParaplannerName: extractedData.assignment?.claimedByParaplannerName ?? null,
      },
      
      // Personal Information - Updated to match form field structure exactly
      personal: {
        // Basic Information
        title: extractedData.personal?.title || null,
        firstName: extractedData.personal?.firstName || null,
        preferredName: extractedData.personal?.preferredName || null,
        middleName: extractedData.personal?.middleName || null,
        surname: extractedData.personal?.surname || null,
        maidenName: extractedData.personal?.maidenName || null,
        gender: extractedData.personal?.gender || null,
        dateOfBirth: extractedData.personal?.dateOfBirth || null,
        
        // Contact Information
        contactNumber: extractedData.personal?.contactNumber || null,
        email: extractedData.personal?.email || null,
        
        // Home Address - Individual components
        homeStreetAddress: extractedData.personal?.homeStreetAddress || null,
        homeSuburb: extractedData.personal?.homeSuburb || null,
        homeCity: extractedData.personal?.homeCity || null,
        homeState: extractedData.personal?.homeState || null,
        homePostcode: extractedData.personal?.homePostcode || null,
        homeCountry: extractedData.personal?.homeCountry || null,
        
        // Different Postal Address
        hasDifferentPostalAddress: extractedData.personal?.hasDifferentPostalAddress || false,
        postalStreetAddress: extractedData.personal?.postalStreetAddress || null,
        postalSuburb: extractedData.personal?.postalSuburb || null,
        postalCity: extractedData.personal?.postalCity || null,
        postalState: extractedData.personal?.postalState || null,
        postalPostcode: extractedData.personal?.postalPostcode || null,
        postalCountry: extractedData.personal?.postalCountry || null,
        
        // Health Information
        height: extractedData.personal?.height || null,
        weight: extractedData.personal?.weight || null,
        bmi: extractedData.personal?.bmi || null,
        smokerStatus: extractedData.personal?.smokerStatus || null,
        healthNotes: extractedData.personal?.healthNotes || null,
        
        // Family Information
        maritalStatus: extractedData.personal?.maritalStatus || null,
        partnerName: extractedData.personal?.partnerName || null,
        partnerDateOfBirth: extractedData.personal?.partnerDateOfBirth || null,
        partnerIncome: extractedData.personal?.partnerIncome ?? null,
        householdIncomeAnnual: extractedData.personal?.householdIncomeAnnual ?? null,
        hasDependants: extractedData.personal?.hasDependants || false,
        dependants: extractedData.personal?.dependants || [], // Array of {firstName, surname, dateOfBirth}
        
        // Other Information
        taxFileNumber: extractedData.personal?.taxFileNumber || null,
        qualification: extractedData.personal?.qualification || null,
        nextOfKin: extractedData.personal?.nextOfKin || null,
        nextOfKinContact: extractedData.personal?.nextOfKinContact || null,
        savingsWeekly: extractedData.personal?.savingsWeekly || null,
        expensesWeekly: extractedData.personal?.expensesWeekly || null,
        hasClaimedInsurance: extractedData.personal?.hasClaimedInsurance || false,
        insuranceClaimDate: extractedData.personal?.insuranceClaimDate || null,
        insuranceClaimNotes: extractedData.personal?.insuranceClaimNotes || null,
        hasBeenBankrupt: extractedData.personal?.hasBeenBankrupt || false,
        bankruptcyDate: extractedData.personal?.bankruptcyDate || null,
        bankruptcyNotes: extractedData.personal?.bankruptcyNotes || null,
        hasWill: extractedData.personal?.hasWill || false
      },
      
      // Employment Information - jobs array only
      employment: {
        jobs: extractedData.employment?.jobs || [],
      },
      
      // Financial Information - Dynamic income structure
      financial: {
        incomes: extractedData.financial?.incomes || [] // Array of income objects: {incomeType, grossIncome, sgEligible, superGuaranteePercent, superGuaranteeDollar}
      },
      
      // Assets & Liabilities - Updated to match form field structure
      assets: {
        items: extractedData.assets?.items || [] // Array of {id, name, value, liability, ppor, interestRate, repaymentAmount, repaymentFrequency, lender, loanTerm, startDate}
      },
      
      // Superannuation - Updated to match form field structure
      superannuation: {
        // Current Funds Array - Complete field structure
        currentFunds: extractedData.superannuation?.currentFunds || [] // Array of fund objects with all fields: {id, fundName, commencementDate, fundAbn, usi, fundType, memberNumber, investmentOption, defensiveAssetsPercent, netOfFees, currentBalance, taxFreeComponent, untaxedElement, fixedAdminFee, adminFeesPercent, investmentFeesPercent, icrFees, merCosts, inclusiveOfInvestmentFeeAndIcr, partialWithdrawalFee, fullWithdrawalFee, adviserFeePercent, adviserFeeCap, contributionFee, minimumBalance, rolloversPermitted, ebaDetails, otherConditions, differentClientName}
      },

      // Contributions - Array-based structure
      contributions: {
        // SG Fields
        sgDestination: extractedData.contributions?.sgDestination || extractedData.superannuation?.sgDestination || '',
        sgFrequency: extractedData.contributions?.sgFrequency || '',
        sgLastReceived: extractedData.contributions?.sgLastReceived || '',
        
        // Contributions Array
        items: extractedData.contributions?.items || [] // Array of contribution objects: {id, type, amount, frequency, destination, lastReceived, noiSubmitted}
      },
      
      // Insurance - Updated to dynamic covers structure
      insurance: {
        covers: extractedData.insurance?.covers || [] // Array of cover objects: {coverType, coverAmount, premium, payee, insurer, definition, monthlyBenefit, waitingPeriod, benefitPeriod}
      },
      
      // Beneficiaries - New section
      beneficiaries: {
        items: extractedData.beneficiaries?.items || [] // Array of beneficiary objects: {id, fundPolicy, firstName, middleName, surname, dateOfBirth, binding, lapsing, percentage}
      },
      
      // Goals - New section
      goals: {
        desiredRetirementAge: extractedData.goals?.desiredRetirementAge || null,
        desiredRetirementIncome: extractedData.goals?.desiredRetirementIncome || null,
        // Goal questions
        next12Months: extractedData.goals?.next12Months || null,
        next1To5Years: extractedData.goals?.next1To5Years || null,
        retirementPlan: extractedData.goals?.retirementPlan || null,
        superLumpSum: extractedData.goals?.superLumpSum || null,
        superImportance: extractedData.goals?.superImportance || null,
        insuranceImportance: extractedData.goals?.insuranceImportance || null,
        previousAdviser: extractedData.goals?.previousAdviser || null
      },
      
      // Risk Profile - New section
      riskProfile: {
        superannuationCashOut: extractedData.riskProfile?.superannuationCashOut || null,
        investmentExperience: extractedData.riskProfile?.investmentExperience || null,
        superannuationReaction: extractedData.riskProfile?.superannuationReaction || null,
        riskToleranceStyle: extractedData.riskProfile?.riskToleranceStyle || null,
        experienceLevel: extractedData.riskProfile?.experienceLevel || null,
        riskScore: extractedData.riskProfile?.riskScore || null,
        riskProfile: extractedData.riskProfile?.riskProfile || null
      },
      
      // Recommendations - New section
      recommendations: {
        optIn: extractedData.recommendations?.optIn || false,
        recommendedCovers: extractedData.recommendations?.recommendedCovers || [], // Array of recommended cover objects: {coverType, coverAmount, premium, payee}
        recommendedContributions: extractedData.recommendations?.recommendedContributions || [], // Array of contribution objects
        contributionActions: extractedData.recommendations?.contributionActions || [], // Array of action objects
        superRollovers: extractedData.recommendations?.superRollovers || [], // Array of rollover objects with retainedAmount and initialRetainedAmount
        insuranceActions: extractedData.recommendations?.insuranceActions || [], // Array of insurance action objects
        strategyReason: extractedData.recommendations?.strategyReason || '' // Reason for insurance strategy (Health, Occupation, Cost)
      },
      
      // Workflow Status - Client workflow stage
      workflowStatus: extractedData.workflowStatus || 'factFinding', // see workflowPhases.js PORTAL_WORKFLOW_STAGES (+ legacy values normalized at read)
      isLost: extractedData.isLost || false, // Boolean flag to indicate if client is lost (preserves workflowStatus)
      isDnq: extractedData.isDnq === true, // Paraplanner DNQ bucket — hidden in portals unless DNQ filter is on
      isArchived: extractedData.isArchived === true, // Advisory archive bucket — hidden unless Archive filter is on
      isTestClient: extractedData.isTestClient === true,
      testClientDevOpenAll: extractedData.testClientDevOpenAll === true,
      testClientCreatedAt: extractedData.testClientCreatedAt || null,
      testClientCreatedBy: extractedData.testClientCreatedBy || null,
    };
    
    clientRecord[CLIENT_LIST_BUCKET_FIELD] = computeClientListBucket(clientRecord);

    await setDoc(doc(db, CLIENTS_COLLECTION, clientId), clientRecord);
    // Every new client gets the standard folders (Onboarding SOA, Projection Comparison, Compliance Documents).
    await ensureClientStandardFoldersQuietly(clientId);
    
    return { success: true, clientId, clientRecord };
    
  } catch (error) {
    console.error('Error creating client record:', error);
    throw new Error(`Failed to create client record: ${error.message}`);
  }
};

// Get client by ID
export const getClientRecord = async (clientId) => {
  try {
    const docRef = doc(db, CLIENTS_COLLECTION, clientId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { success: true, client: docSnap.data() };
    } else {
      throw new Error('Client not found');
    }
  } catch (error) {
    console.error('Error getting client record:', error);
    throw new Error(`Failed to get client record: ${error.message}`);
  }
};

// Recursively remove undefined values from an object (Firestore doesn't accept undefined)
// Firestore sentinels (e.g. serverTimestamp()) must be passed through unchanged so the server can resolve them
const MAX_SANITIZE_DEPTH = 64;

export const removeUndefinedValues = (obj, seen = new WeakSet(), depth = 0) => {
  if (obj === undefined) {
    return null;
  }
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (depth >= MAX_SANITIZE_DEPTH) {
    return null;
  }
  if (typeof obj.toDate === 'function') {
    return obj.toDate().toISOString();
  }
  if (obj && typeof obj === 'object' && '_methodName' in obj) {
    return obj;
  }
  // `seen` holds only the current ancestor chain, so true cycles return null while
  // shared references (the same object appearing in two places) serialize normally.
  // A persistent set here previously nulled the second occurrence of shared cover
  // objects, corrupting arrays in Firestore.
  if (seen.has(obj)) {
    return null;
  }
  seen.add(obj);

  let result;
  if (Array.isArray(obj)) {
    // Array.from, not map: map skips the holes of a sparse array (an action set
    // at index 2 of an empty list), and Firestore rejects the whole write over them.
    result = Array.from(obj, (item) => removeUndefinedValues(item, seen, depth + 1));
  } else {
    result = {};
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      const value = obj[key];
      if (value === undefined) continue;
      result[key] = removeUndefinedValues(value, seen, depth + 1);
    }
  }

  seen.delete(obj);
  return result;
};

// Add audit log entry for a client (workflow change, email sent, document generated)
export const addAuditLogEntry = async (clientId, { userId, userDisplayName, action, details = {} }) => {
  try {
    const auditRef = collection(db, CLIENTS_COLLECTION, clientId, 'auditLog');
    // Firestore does not accept undefined; strip it from details
    const cleanedDetails = typeof details === 'object' && details !== null
      ? removeUndefinedValues({ ...details })
      : {};
    await addDoc(auditRef, {
      timestamp: serverTimestamp(),
      userId: userId ?? null,
      userDisplayName: userDisplayName ?? null,
      action,
      details: cleanedDetails
    });
  } catch (error) {
    console.error('Error adding audit log entry:', error);
  }
};

// Admin config changes have no client, so they live in a top-level collection
// (adminAuditLog) with the same doc shape. Coarse by design: one entry per
// save with { configArea, summary } — no field-level diffs.
export const addAdminAuditLogEntry = async ({ userId, userDisplayName, action = 'admin_config_changed', details = {} }) => {
  try {
    const cleanedDetails = typeof details === 'object' && details !== null
      ? removeUndefinedValues({ ...details })
      : {};
    await addDoc(collection(db, 'adminAuditLog'), {
      timestamp: serverTimestamp(),
      userId: userId ?? null,
      userDisplayName: userDisplayName ?? null,
      action,
      details: cleanedDetails,
    });
  } catch (error) {
    console.error('Error adding admin audit log entry:', error);
  }
};

export const getRecentAdminAuditLogEntries = async ({ max = 200 } = {}) => {
  const q = query(collection(db, 'adminAuditLog'), orderBy('timestamp', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Subscribe to a client's audit log (newest first, capped — pass a callback
// second arg and read entries.length === max to detect truncation)
export const AUDIT_LOG_SUBSCRIPTION_LIMIT = 500;
export const subscribeToClientAuditLog = (clientId, callback, { max = AUDIT_LOG_SUBSCRIPTION_LIMIT } = {}) => {
  if (!clientId) return () => {};
  const auditRef = collection(db, CLIENTS_COLLECTION, clientId, 'auditLog');
  const q = query(auditRef, orderBy('timestamp', 'desc'), limit(max));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const entries = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(entries);
  }, (error) => {
    console.error('Error subscribing to audit log:', error);
    callback([]);
  });
  return unsubscribe;
};

const FILE_NOTES_SUBCOLLECTION = 'fileNotes';

function mapFileNoteDoc(d) {
  const data = d.data();
  const out = { id: d.id, ...data };
  if (data.createdAt && typeof data.createdAt.toDate === 'function') {
    out.createdAt = data.createdAt.toDate().toISOString();
  }
  if (data.lastModifiedAt && typeof data.lastModifiedAt.toDate === 'function') {
    out.lastModifiedAt = data.lastModifiedAt.toDate().toISOString();
  }
  if (data.approvedAt && typeof data.approvedAt.toDate === 'function') {
    out.approvedAt = data.approvedAt.toDate().toISOString();
  }
  return out;
}

// Subscribe to a client's file notes (newest last-modified first)
export const subscribeToClientFileNotes = (clientId, callback) => {
  if (!clientId) return () => {};
  const ref = collection(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION);
  const q = query(ref, orderBy('lastModifiedAt', 'desc'));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs.map(mapFileNoteDoc);
    callback(notes);
  }, (error) => {
    console.error('Error subscribing to file notes:', error);
    callback([]);
  });
  return unsubscribe;
};

// Create a file note for a client
export const createFileNote = async (clientId, payload, userId) => {
  const ref = collection(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION);
  let createdByName = '';
  let lastModifiedByName = '';
  let teamName = null;
  if (userId) {
    const userTeamServiceModule = await import('./userTeamService');
    const userTeamService = userTeamServiceModule.default;
    createdByName = userTeamService.getFullName(userId) || '';
    lastModifiedByName = createdByName;
    teamName = userTeamService.getUserTeamName(userId);
  }
  const data = removeUndefinedValues({
    category: payload.category ?? null,
    title: payload.title ?? null,
    body: payload.body ?? null,
    attachments: Array.isArray(payload.attachments) ? payload.attachments : [],
    team: teamName ?? payload.team ?? null,
    source: 'manual',
    approvalStatus: null,
    assignedTo: null,
    createdBy: userId ?? null,
    createdByName: createdByName || null,
    createdAt: serverTimestamp(),
    lastModifiedBy: userId ?? null,
    lastModifiedByName: lastModifiedByName || null,
    lastModifiedAt: serverTimestamp()
  });
  const docRef = await addDoc(ref, data);
  return { success: true, id: docRef.id };
};

// Update a file note
export const updateFileNote = async (clientId, noteId, updates, userId) => {
  const docRef = doc(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION, noteId);
  let lastModifiedByName = null;
  if (userId) {
    const userTeamServiceModule = await import('./userTeamService');
    const userTeamService = userTeamServiceModule.default;
    lastModifiedByName = userTeamService.getFullName(userId);
  }
  const allowed = { category: updates.category, title: updates.title, body: updates.body, team: updates.team, attachments: Array.isArray(updates.attachments) ? updates.attachments : undefined };
  const data = removeUndefinedValues({
    ...allowed,
    lastModifiedBy: userId ?? null,
    lastModifiedByName,
    lastModifiedAt: serverTimestamp()
  });
  await updateDoc(docRef, data);
  return { success: true };
};

// Delete a file note
export const deleteFileNote = async (clientId, noteId, userId = null) => {
  const docRef = doc(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION, noteId);
  await deleteDoc(docRef);
  return { success: true };
};

/** Shape used in task modals — mirrors linked file note documents. */
export function fileNoteDocToTaskNote(note) {
  if (!note?.id) return null;
  return {
    id: note.id,
    fileNoteId: note.id,
    title: note.title || 'Task Note',
    body: note.body || '',
    createdAt: note.createdAt || note.lastModifiedAt || '',
    createdBy: note.createdBy || '',
    createdByName: note.createdByName || note.lastModifiedByName || '',
    // Workflow amendment requests are flagged so task boards can style them.
    ...(note.kind ? { kind: note.kind } : {}),
    ...(note.amendmentRound ? { amendmentRound: note.amendmentRound } : {}),
    // Which task in the run wrote it — the shared run board labels cards with it.
    ...(note.workflowTaskId ? { workflowTaskId: note.workflowTaskId } : {}),
    ...(note.workflowRunId ? { workflowRunId: note.workflowRunId } : {}),
    ...(Array.isArray(note.attachments) && note.attachments.length ? { attachments: note.attachments } : {}),
  };
}

/** Fetch file notes by id (linked from tasks via fileNoteIds). */
export const getFileNotesByIds = async (clientId, ids) => {
  if (!clientId || !ids?.length) return [];
  const unique = [...new Set(ids.filter(Boolean))];
  const snaps = await Promise.all(
    unique.map((id) => getDoc(doc(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION, id)))
  );
  return snaps
    .filter((snap) => snap.exists())
    .map(mapFileNoteDoc)
    .map(fileNoteDocToTaskNote)
    .filter(Boolean);
};

/**
 * Live file notes for a whole workflow run: every task in the run shares one
 * board, so the query is by `workflowRunId` rather than each task's ids.
 * Equality-only, sorted in memory — no composite index needed.
 */
export const subscribeToRunFileNotes = (clientId, runId, callback) => {
  if (!clientId || !runId) {
    callback([]);
    return () => {};
  }
  const ref = collection(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION);
  const q = query(ref, where('workflowRunId', '==', runId));
  return onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs
      .map(mapFileNoteDoc)
      .map(fileNoteDocToTaskNote)
      .filter(Boolean)
      .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());
    callback(notes);
  }, (error) => {
    console.error('Error subscribing to workflow run file notes:', error);
    callback([]);
  });
};

/**
 * Live file notes for a task (source of truth). Falls back to embedded taskNotes when no ids.
 */
export const subscribeToTaskFileNotes = (clientId, fileNoteIds, callback) => {
  if (!clientId) {
    callback([]);
    return () => {};
  }
  const ids = [...new Set((fileNoteIds || []).filter(Boolean))];
  if (ids.length === 0) {
    callback([]);
    return () => {};
  }

  const notesById = {};
  const emit = () => {
    const notes = ids
      .map((id) => notesById[id])
      .filter(Boolean)
      .sort(
        (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
      );
    callback(notes);
  };

  const unsubscribes = ids.map((noteId) =>
    onSnapshot(
      doc(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION, noteId),
      (snap) => {
        if (snap.exists()) {
          const mapped = fileNoteDocToTaskNote(mapFileNoteDoc(snap));
          if (mapped) notesById[noteId] = mapped;
        } else {
          delete notesById[noteId];
        }
        emit();
      },
      (error) => {
        console.error('Error subscribing to task file note:', noteId, error);
        delete notesById[noteId];
        emit();
      }
    )
  );

  return () => {
    unsubscribes.forEach((unsub) => unsub());
  };
};

// Create a system-generated file note that requires user approval
/** `extra`: more fields for the note, e.g. { workflowRunId, workflowTaskId } to show it on a workflow run's board. */
export const createAutoFileNote = async (clientId, { category, title, body, attachments, assignedTo, clientName, extra = null }) => {
  const ref = collection(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION);
  let assignedToName = '';
  let teamName = null;
  if (assignedTo) {
    const mod = await import('./userTeamService');
    assignedToName = mod.default.getFullName(assignedTo) || '';
    teamName = mod.default.getUserTeamName(assignedTo);
  }
  const data = removeUndefinedValues({
    category: category ?? null,
    title: title ?? null,
    body: body ?? null,
    attachments: Array.isArray(attachments) ? attachments : [],
    team: teamName ?? null,
    source: 'auto',
    approvalStatus: 'pending',
    assignedTo: assignedTo || null,
    assignedToName: assignedToName || null,
    clientName: clientName || null,
    approvedBy: null,
    approvedByName: null,
    approvedAt: null,
    createdBy: assignedTo || null,
    createdByName: assignedToName || null,
    createdAt: serverTimestamp(),
    lastModifiedBy: assignedTo || null,
    lastModifiedByName: assignedToName || null,
    lastModifiedAt: serverTimestamp(),
    ...(extra && typeof extra === 'object' ? extra : {}),
  });
  const docRef = await addDoc(ref, data);
  return { success: true, id: docRef.id };
};

// Approve a pending file note (saves edits + marks approved)
export const approveFileNote = async (clientId, noteId, updates, userId) => {
  const docRef = doc(db, CLIENTS_COLLECTION, clientId, FILE_NOTES_SUBCOLLECTION, noteId);
  let approvedByName = null;
  if (userId) {
    const mod = await import('./userTeamService');
    approvedByName = mod.default.getFullName(userId);
  }
  const allowed = {
    category: updates.category,
    title: updates.title,
    body: updates.body,
    attachments: Array.isArray(updates.attachments) ? updates.attachments : undefined,
  };
  const data = removeUndefinedValues({
    ...allowed,
    approvalStatus: 'approved',
    approvedBy: userId ?? null,
    approvedByName: approvedByName || null,
    approvedAt: serverTimestamp(),
    lastModifiedBy: userId ?? null,
    lastModifiedByName: approvedByName || null,
    lastModifiedAt: serverTimestamp(),
  });
  await updateDoc(docRef, data);
  return { success: true };
};

// Subscribe to all pending file notes assigned to a specific user (across all clients)
export const subscribeToPendingFileNotes = (userEmail, callback) => {
  if (!userEmail) return () => {};
  const cg = collectionGroup(db, FILE_NOTES_SUBCOLLECTION);
  const q = query(cg, where('approvalStatus', '==', 'pending'), where('assignedTo', '==', userEmail));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs.map(d => ({
      ...mapFileNoteDoc(d),
      clientId: d.ref.parent.parent.id,
    }));
    callback(notes);
  }, (error) => {
    console.error('Error subscribing to pending file notes:', error);
    callback([]);
  });
  return unsubscribe;
};

// Subscribe to every pending file note across all clients (compliance dashboard).
// Firm scoping happens caller-side via the clientId -> client join (notes carry no team id).
export const subscribeToAllPendingFileNotes = (callback) => {
  const cg = collectionGroup(db, FILE_NOTES_SUBCOLLECTION);
  const q = query(cg, where('approvalStatus', '==', 'pending'));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs.map(d => ({
      ...mapFileNoteDoc(d),
      clientId: d.ref.parent.parent.id,
    }));
    callback(notes);
  }, (error) => {
    console.error('Error subscribing to all pending file notes:', error);
    callback([]);
  });
  return unsubscribe;
};

const SMS_MESSAGES_SUBCOLLECTION = 'smsMessages';
const CALL_LOGS_SUBCOLLECTION = 'callLogs';

function mapSmsDoc(d) {
  const data = d.data();
  const out = { id: d.id, ...data };
  if (data.createdAt && typeof data.createdAt.toDate === 'function') {
    out.createdAt = data.createdAt.toDate().toISOString();
  }
  return out;
}

function mapCallLogDoc(d, clientId) {
  const data = d.data();
  const out = { id: d.id, clientId: data.clientId || clientId, ...data };
  if (data.createdAt && typeof data.createdAt.toDate === 'function') {
    out.createdAt = data.createdAt.toDate().toISOString();
  }
  if (data.startedAt && typeof data.startedAt === 'string') {
    out.startedAt = data.startedAt;
  }
  return out;
}

export const subscribeToClientSmsMessages = (clientId, callback) => {
  if (!clientId) return () => {};
  const ref = collection(db, CLIENTS_COLLECTION, clientId, SMS_MESSAGES_SUBCOLLECTION);
  const q = query(ref, orderBy('createdAt', 'asc'));
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      callback(snapshot.docs.map(mapSmsDoc));
    },
    (error) => {
      console.error('Error subscribing to SMS messages:', error);
      callback([]);
    }
  );
  return unsubscribe;
};

export const subscribeToTeamSmsMessages = (teamSmsNumber, callback) => {
  if (!teamSmsNumber) return () => {};
  const cg = collectionGroup(db, SMS_MESSAGES_SUBCOLLECTION);
  const q = query(cg, where('teamSmsNumber', '==', teamSmsNumber), orderBy('createdAt', 'desc'));
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs.map((d) => ({
        ...mapSmsDoc(d),
        clientId: d.ref.parent.parent?.id || d.data().clientId,
      }));
      callback(messages);
    },
    (error) => {
      console.error('Error subscribing to team SMS messages:', error);
      callback([]);
    }
  );
  return unsubscribe;
};

const SCHEDULED_SMS_COLLECTION = 'scheduledSms';

/** Firestore Timestamps out, ISO strings in — matching mapSmsDoc. */
const mapScheduledSmsDoc = (docSnap) => {
  const data = docSnap.data() || {};
  const iso = (t) => (t && typeof t.toDate === 'function' ? t.toDate().toISOString() : t || null);
  return {
    ...data,
    id: docSnap.id,
    requestedSendAt: iso(data.requestedSendAt),
    sendAt: iso(data.sendAt),
    sentAt: iso(data.sentAt),
    deliveredAt: iso(data.deliveredAt),
    createdAt: iso(data.createdAt),
    updatedAt: iso(data.updatedAt),
    cancelledAt: iso(data.cancelledAt),
  };
};

/**
 * Live scheduled-SMS queue for one sending line. Rows are server-written
 * (firestore.rules denies client writes) and read here for the Scheduled tab.
 */
export const subscribeToScheduledSms = (fromNumber, callback) => {
  if (!fromNumber) return () => {};
  const ref = collection(db, SCHEDULED_SMS_COLLECTION);
  const q = query(ref, where('fromNumber', '==', fromNumber), orderBy('sendAt', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map(mapScheduledSmsDoc)),
    (error) => {
      console.error('Error subscribing to scheduled SMS:', error);
      callback([]);
    }
  );
};

/** The pending queue for one client, for the strip above the composer. */
export const subscribeToClientScheduledSms = (clientId, callback) => {
  if (!clientId) return () => {};
  const ref = collection(db, SCHEDULED_SMS_COLLECTION);
  const q = query(ref, where('clientId', '==', clientId), orderBy('sendAt', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => callback(snapshot.docs.map(mapScheduledSmsDoc)),
    (error) => {
      console.error('Error subscribing to client scheduled SMS:', error);
      callback([]);
    }
  );
};

export const subscribeToClientCallLogs = (clientId, callback) => {
  if (!clientId) return () => {};
  const ref = collection(db, CLIENTS_COLLECTION, clientId, CALL_LOGS_SUBCOLLECTION);
  const unsubscribe = onSnapshot(
    ref,
    (snapshot) => {
      const logs = snapshot.docs.map((docSnap) => mapCallLogDoc(docSnap, clientId));
      logs.sort((a, b) => new Date(a.startedAt || 0) - new Date(b.startedAt || 0));
      callback(logs);
    },
    (error) => {
      console.error('Error subscribing to call logs:', error);
      callback([]);
    }
  );
  return unsubscribe;
};

/** Team call logs (linked + unlinked) for the missed-call/voicemail feed. */
export const subscribeToTeamCallLogs = (teamSmsNumber, callback, { max = 400 } = {}) => {
  if (!teamSmsNumber) return () => {};
  const cg = collectionGroup(db, CALL_LOGS_SUBCOLLECTION);
  const q = query(
    cg,
    where('teamSmsNumber', '==', teamSmsNumber),
    orderBy('startedAt', 'desc'),
    limit(max)
  );
  const unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      const logs = snapshot.docs.map((d) =>
        mapCallLogDoc(d, d.ref.parent.parent?.id || null)
      );
      callback(logs);
    },
    (error) => {
      console.error('Error subscribing to team call logs:', error);
      callback([]);
    }
  );
  return unsubscribe;
};

/** entries: [{ clientId|null, callLogId }] — unlinked logs live in the top-level collection. */
export const markCallLogsRead = async (entries, userEmail) => {
  if (!userEmail || !Array.isArray(entries) || entries.length === 0) {
    return { success: true };
  }
  await Promise.all(
    entries
      .filter((entry) => entry?.callLogId)
      .map((entry) =>
        updateDoc(
          entry.clientId
            ? doc(db, CLIENTS_COLLECTION, entry.clientId, CALL_LOGS_SUBCOLLECTION, entry.callLogId)
            : doc(db, CALL_LOGS_SUBCOLLECTION, entry.callLogId),
          { readBy: arrayUnion(userEmail) }
        )
      )
  );
  return { success: true };
};

export const markSmsMessagesRead = async (clientId, messageIds, userEmail) => {
  if (!clientId || !userEmail || !Array.isArray(messageIds) || messageIds.length === 0) {
    return { success: true };
  }
  await Promise.all(
    messageIds.map((messageId) =>
      updateDoc(doc(db, CLIENTS_COLLECTION, clientId, SMS_MESSAGES_SUBCOLLECTION, messageId), {
        readBy: arrayUnion(userEmail),
      })
    )
  );
  return { success: true };
};

function extractAssignedAdvisoryFromUpdates(updates = {}) {
  if (updates.assignment && typeof updates.assignment === 'object' && 'assignedAdvisory' in updates.assignment) {
    return updates.assignment.assignedAdvisory;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'assignment.assignedAdvisory')) {
    return updates['assignment.assignedAdvisory'];
  }
  return undefined;
}

function extractAssignedMarketingTeamFromUpdates(updates = {}) {
  if (updates.assignment && typeof updates.assignment === 'object' && 'assignedMarketingTeam' in updates.assignment) {
    return updates.assignment.assignedMarketingTeam;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'assignment.assignedMarketingTeam')) {
    return updates['assignment.assignedMarketingTeam'];
  }
  return undefined;
}

// Update client record
export const updateClientRecord = async (clientId, updates, userId = null, options = {}) => {
  try {
    const docRef = doc(db, CLIENTS_COLLECTION, clientId);
    
    // Remove undefined values before saving (Firestore doesn't accept undefined)
    const cleanedUpdates = removeUndefinedValues(updates);
    
    const updateData = {
      ...cleanedUpdates,
      lastModified: serverTimestamp()
    };
    
    let userDisplayName = null;
    if (userId) {
      const userTeamServiceModule = await import('./userTeamService');
      const userTeamService = userTeamServiceModule.default;
      updateData.lastModifiedBy = userId;
      userDisplayName = userTeamService.getUserName(userId);
      updateData.lastModifiedByName = userDisplayName;

      // Marketing users may only assign advice teams authorized for their team.
      const nextAdvisory = extractAssignedAdvisoryFromUpdates(cleanedUpdates);
      if (nextAdvisory !== undefined && nextAdvisory) {
        const userConfig = userTeamService.getUserConfig(userId);
        if (userConfig?.role === 'marketing') {
          let marketingForAuth = extractAssignedMarketingTeamFromUpdates(cleanedUpdates);
          if (marketingForAuth === undefined) {
            try {
              const existingSnap = await getDoc(docRef);
              marketingForAuth = existingSnap.exists()
                ? (existingSnap.data()?.assignment?.assignedMarketingTeam || '')
                : '';
            } catch {
              marketingForAuth = '';
            }
          }
          const authMarketingTeam = userTeamService.resolveMarketingTeamForAuthorization(
            userId,
            marketingForAuth || '',
          );
          if (!userTeamService.isAdvisoryAuthorizedForMarketingTeam(authMarketingTeam, nextAdvisory)) {
            throw new Error(
              'That advice team is not authorized for this marketing team.',
            );
          }
        }
      }
    }
    
    await updateDoc(docRef, updateData);

    // A firm assignment starts the firm's Onboarding workflow for clients that
    // were created without one (server no-ops unless the client is pending).
    if (extractAssignedAdvisoryFromUpdates(cleanedUpdates)) {
      import('./workflowsApi')
        .then(({ autoStartOnboardingWorkflows }) => autoStartOnboardingWorkflows(clientId, { trigger: 'firm-assigned' }))
        .catch((err) => console.error('Pending onboarding workflow start failed:', err));
    }
    
    // Audit log: workflow change when workflowStatus or isLost is updated
    if (!options.skipWorkflowAudit && (cleanedUpdates.workflowStatus !== undefined || cleanedUpdates.isLost !== undefined)) {
      const workflowContext = options._workflowContext || {};
      const workflowAuditMeta = options.workflowAuditMeta || {};
      const correlationId = workflowAuditMeta.correlationId || `workflow-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const fromWorkflowStatus = workflowContext.fromWorkflowStatus ?? null;
      const fromIsLost = workflowContext.fromIsLost ?? null;
      const toWorkflowStatus = cleanedUpdates.workflowStatus !== undefined ? cleanedUpdates.workflowStatus : fromWorkflowStatus;
      const toIsLost = cleanedUpdates.isLost !== undefined ? cleanedUpdates.isLost : fromIsLost;

      const workflowStatusChanged = cleanedUpdates.workflowStatus !== undefined
        ? cleanedUpdates.workflowStatus !== fromWorkflowStatus
        : false;
      const isLostChanged = cleanedUpdates.isLost !== undefined
        ? cleanedUpdates.isLost !== fromIsLost
        : false;

      // Avoid noisy audit entries when a write includes workflow keys but values are unchanged.
      if (!workflowStatusChanged && !isLostChanged) {
        return { success: true };
      }

      await addAuditLogEntry(clientId, {
        userId,
        userDisplayName,
        action: 'workflow_change',
        details: {
          workflowStatus: toWorkflowStatus,
          isLost: toIsLost,
          fromWorkflowStatus,
          toWorkflowStatus,
          fromIsLost,
          toIsLost,
          initiatedBy: workflowAuditMeta.initiatedBy || (userId ? 'user' : 'system'),
          triggerAction: workflowAuditMeta.triggerAction || null,
          reason: workflowAuditMeta.reason || null,
          correlationId
        }
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error updating client record:', error);
    throw new Error(`Failed to update client record: ${error.message}`);
  }
};

// Get all clients (global access for all authenticated users)
export const getAllClients = async () => {
  try {
    // First try with the ordered query
    const q = query(
      collection(db, CLIENTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const clients = [];
    
    querySnapshot.forEach((doc) => {
      clients.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return { success: true, clients };
  } catch (error) {
    console.error('Error getting all clients:', error);
    
    // If index error, try without orderBy
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      try {
        const querySnapshot = await getDocs(collection(db, CLIENTS_COLLECTION));
        const clients = [];
        
        querySnapshot.forEach((doc) => {
          clients.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort manually by creation date
        clients.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        
        return { success: true, clients };
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
        throw new Error(`Failed to get all clients: ${fallbackError.message}`);
      }
    }
    
    throw new Error(`Failed to get all clients: ${error.message}`);
  }
};

// Subscribe to all clients with real-time updates
export const subscribeToAllClients = (callback) => {
  let unsubscribe = null;
  let fallbackUnsubscribe = null;
  
  try {
    // First try with the ordered query
    const q = query(
      collection(db, CLIENTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const clients = [];
        querySnapshot.forEach((doc) => {
          clients.push({
            id: doc.id,
            ...doc.data()
          });
        });
        callback({ success: true, clients });
      },
      (error) => {
        console.error('Error in clients subscription:', error);
        
        // If index error, try without orderBy
        if (error.code === 'failed-precondition' || error.message.includes('index')) {
          // Unsubscribe from the ordered query if it's active
          if (unsubscribe) {
            unsubscribe();
          }
          
          // Set up fallback subscription without orderBy
          try {
            fallbackUnsubscribe = onSnapshot(
              collection(db, CLIENTS_COLLECTION),
              (querySnapshot) => {
                const clients = [];
                querySnapshot.forEach((doc) => {
                  clients.push({
                    id: doc.id,
                    ...doc.data()
                  });
                });
                
                // Sort manually by creation date
                clients.sort((a, b) => {
                  const dateA = a.createdAt?.toDate?.() || new Date(0);
                  const dateB = b.createdAt?.toDate?.() || new Date(0);
                  return dateB - dateA;
                });
                
                callback({ success: true, clients });
              },
              (fallbackError) => {
                console.error('Fallback subscription also failed:', fallbackError);
                callback({ success: false, error: fallbackError.message });
              }
            );
          } catch (fallbackError) {
            console.error('Error setting up fallback subscription:', fallbackError);
            callback({ success: false, error: fallbackError.message });
          }
        } else {
          callback({ success: false, error: error.message });
        }
      }
    );
    
    // Return unsubscribe function that cleans up both subscriptions
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      if (fallbackUnsubscribe) {
        fallbackUnsubscribe();
      }
    };
  } catch (error) {
    console.error('Error setting up clients subscription:', error);
    callback({ success: false, error: error.message });
    // Return a no-op unsubscribe function
    return () => {};
  }
};

// Keep the old function for backward compatibility (now just calls getAllClients)
export const getUserClients = async (userId) => {
  return getAllClients();
};

// Deleting a client: use deleteClientPermanently (services/clientRecordApi.js).
// A browser-side delete of clients/{id} leaves its subcollections behind, and
// orphaned workflowTasks keep showing in assignees' task lists.

// Update document notes for a client
export const updateClientDocumentNotes = async (clientId, documentNotes, userId) => {
  try {
    const docRef = doc(db, CLIENTS_COLLECTION, clientId);
    const updateData = {
      documentNotes: {
        ...documentNotes,
        lastUpdated: serverTimestamp(),
        updatedBy: userId
      },
      lastModified: serverTimestamp()
    };
    
    await updateDoc(docRef, updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating document notes:', error);
    throw new Error(`Failed to update document notes: ${error.message}`);
  }
};

// Get document notes for a client
export const getClientDocumentNotes = async (clientId) => {
  try {
    const docRef = doc(db, CLIENTS_COLLECTION, clientId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return { 
        success: true, 
        documentNotes: data.documentNotes || {
          adviserSummary: '',
          fileNote: '',
          followUpEmail: '',
          insuranceBooking: '',
          revisedTermsNote: '',
          lastUpdated: null,
          updatedBy: null
        }
      };
    } else {
      throw new Error('Client not found');
    }
  } catch (error) {
    console.error('Error getting document notes:', error);
    throw new Error(`Failed to get document notes: ${error.message}`);
  }
};

// Clean up stale data from existing client records
export const cleanupStaleClientData = async (clientId, userId = null) => {
  try {
    const docRef = doc(db, CLIENTS_COLLECTION, clientId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Client not found');
    }
    
    const clientData = docSnap.data();
    const updateData = {
      lastModified: serverTimestamp()
    };
    
    // Add user tracking if userId is provided
    if (userId) {
      const userTeamServiceModule = await import('./userTeamService');
      const userTeamService = userTeamServiceModule.default;
      updateData.lastModifiedBy = userId;
      updateData.lastModifiedByName = userTeamService.getUserName(userId);
    }
    
    // Clean up superannuation section
    if (clientData.superannuation) {
      const cleanedSuperannuation = { ...clientData.superannuation };
      
      // Remove stale nested contributions structure
      if (cleanedSuperannuation.contributions) {
        // Migrate important data to flat structure if not already present
        if (!cleanedSuperannuation.salarySacrifice && cleanedSuperannuation.contributions.salarySacrifice !== undefined) {
          cleanedSuperannuation.salarySacrifice = cleanedSuperannuation.contributions.salarySacrifice;
        }
        if (!cleanedSuperannuation.salarySacrificeAmount && cleanedSuperannuation.contributions.salarySacrificeAmount) {
          cleanedSuperannuation.salarySacrificeAmount = cleanedSuperannuation.contributions.salarySacrificeAmount;
        }
        if (!cleanedSuperannuation.salarySacrificeFrequency && cleanedSuperannuation.contributions.salarySacrificeFrequency) {
          cleanedSuperannuation.salarySacrificeFrequency = cleanedSuperannuation.contributions.salarySacrificeFrequency;
        }
        if (!cleanedSuperannuation.concessional && cleanedSuperannuation.contributions.concessional !== undefined) {
          cleanedSuperannuation.concessional = cleanedSuperannuation.contributions.concessional;
        }
        if (!cleanedSuperannuation.concessionalAmount && cleanedSuperannuation.contributions.concessionalAmount) {
          cleanedSuperannuation.concessionalAmount = cleanedSuperannuation.contributions.concessionalAmount;
        }
        if (!cleanedSuperannuation.nonConcessional && cleanedSuperannuation.contributions.nonConcessional !== undefined) {
          cleanedSuperannuation.nonConcessional = cleanedSuperannuation.contributions.nonConcessional;
        }
        if (!cleanedSuperannuation.nonConcessionalAmount && cleanedSuperannuation.contributions.nonConcessionalAmount) {
          cleanedSuperannuation.nonConcessionalAmount = cleanedSuperannuation.contributions.nonConcessionalAmount;
        }
        
        // Remove the stale nested structure
        delete cleanedSuperannuation.contributions;
      }
      
      // Remove other stale fields
      delete cleanedSuperannuation.currentInvestmentOption;
      delete cleanedSuperannuation.currentBalance;
      
      updateData.superannuation = cleanedSuperannuation;
    }
    
    // Clean up recommendations section
    if (clientData.recommendations) {
      const cleanedRecommendations = { ...clientData.recommendations };
      
      // Remove stale top-level fields
      delete cleanedRecommendations.superStrategy;
      delete cleanedRecommendations.insuranceStrategy;
      delete cleanedRecommendations.retainAmount;
      
      updateData.recommendations = cleanedRecommendations;
    }
    
    // Remove entire stale sections
    if (clientData.projections) {
      updateData.projections = null; // Set to null to remove
    }
    if (clientData.processing) {
      updateData.processing = null; // Set to null to remove
    }
    if (clientData.documentNotes) {
      updateData.documentNotes = null; // Set to null to remove
    }
    
    // Update the document
    await updateDoc(docRef, updateData);
    
    return { success: true, cleanedFields: Object.keys(updateData).filter(key => key !== 'lastModified' && key !== 'lastModifiedBy' && key !== 'lastModifiedByName') };
    
  } catch (error) {
    console.error('Error cleaning up stale client data:', error);
    throw new Error(`Failed to clean up stale client data: ${error.message}`);
  }
};

// Clean up all client records (use with caution)
export const cleanupAllStaleClientData = async (userId) => {
  try {
    const { clients } = await getAllClients();
    const results = [];
    
    for (const client of clients) {
      try {
        const result = await cleanupStaleClientData(client.id, userId);
        results.push({ clientId: client.id, success: true, ...result });
      } catch (error) {
        results.push({ clientId: client.id, success: false, error: error.message });
      }
    }
    
    return { success: true, results };
    
  } catch (error) {
    console.error('Error cleaning up all client data:', error);
    throw new Error(`Failed to clean up all client data: ${error.message}`);
  }
};